import { Injectable } from '@angular/core';
import { GroupRowArgs, PageChangeEvent } from '@progress/kendo-angular-grid';
import {
  GroupDescriptor,
  GroupResult,
  State,
} from '@progress/kendo-data-query';
import { PageChangeEventExtended } from './grid-grouping-pager.component';
import { Observable, of } from 'rxjs';
import { ODataResult } from '@ups/xplat/features';

@Injectable({
  providedIn: 'root',
})
export class GridGroupingPagerService<T> {
  lastGroupExpandIndex: string = null;
  groupIndexAndLastPageUsed: Record<string, PageChangeEventExtended<T>> = {};
  initialGroupPageSize = 25;
  startPagingFromItemsNumber: number = this.initialGroupPageSize; // groups that have more than this number of items will get the paging activated

  getGroupPage(groupIndex: string): PageChangeEventExtended<T> {
    if (!this.groupIndexAndLastPageUsed[groupIndex]) {
      this.groupIndexAndLastPageUsed[groupIndex] = {
        skip: 0,
        take: this.initialGroupPageSize,
        totalCount: 0,
      } as PageChangeEventExtended<T>;
    }

    return this.groupIndexAndLastPageUsed[groupIndex];
  }

  pageSize(groupIndex: string): number {
    return this.getGroupPage(groupIndex).take;
  }

  skip(groupIndex: string): number {
    return this.getGroupPage(groupIndex).skip;
  }

  totalCount(groupIndex: string, totalCount: number): number {
    return totalCount || this.getGroupPage(groupIndex).totalCount;
  }

  /**
   * Handles page change for a group. Uses in-memory page.data when available (no API call).
   * With 2+ columns, child group items may have been loaded via "load levels below" and never
   * stored in page.data; we initialize page.data from group.items so paging stays in-memory.
   * @returns true if we used cached data (slice only); false if we cleared items and caller should trigger load.
   */
  onPageChange(
    event: PageChangeEvent,
    group: GroupResult,
    groupIndex: string,
    totalCount?: number
  ): boolean {
    const page: PageChangeEventExtended<T> = this.getGroupPage(groupIndex);
    const defaultAggregates = group?.aggregates as
      | Record<string, { count?: number }>
      | undefined;
    const lastKnownTotalCount =
      totalCount ??
      page.totalCount ??
      defaultAggregates?.default?.count ??
      (Array.isArray(group?.items) ? group.items.length : 0);
    page.skip = event.skip;
    page.take = event.take;
    page.totalCount = lastKnownTotalCount;
    this.groupIndexAndLastPageUsed[groupIndex] = page;
    this.lastGroupExpandIndex = groupIndex;

    // If we don't have page.data but the grid already has items (e.g. from loadLevelsBelow with 2+ columns), use them.
    if (!page.data?.length && (group?.items?.length ?? 0) > 0) {
      page.data = (group.items as T[]) || [];
    }

    if (page.data?.length) {
      group.items = page.data.slice(page.skip, page.skip + page.take);
      return true;
    }
    group.items = [];
    return false;
  }

  /**
   * Is last group expanded (in case data in the grid are grouped by multiple columns the method returns true if groupIndex is the last group - so the one which shows actual results)
   */
  isLastGroup(groups: GroupDescriptor[], groupIndex: string): boolean {
    return !!groups?.length && groups.length === groupIndex.split('_').length;
  }

  consolidateGroupPaging() {
    // During filter change the kendo generates odata query for all opened groups in the grid by condition where column in (...,..,..)
    // and it re-expands the already expanded groups so we need to consolidate the pagesize as now we had only 1 query for all groups
    for (const key of Object.keys(this.groupIndexAndLastPageUsed)) {
      const pageData: PageChangeEventExtended<T> =
        this.groupIndexAndLastPageUsed[key];
      pageData.skip = 0;
      pageData.take = this.initialGroupPageSize;
    }
  }

  resetGroupPaging() {
    this.lastGroupExpandIndex = null;
    this.groupIndexAndLastPageUsed = {};
  }

  onGroupExpand(groups: GroupDescriptor[], groupRowArgs: GroupRowArgs) {
    this.lastGroupExpandIndex = null;
    if (
      this.isLastGroup(groups, groupRowArgs.groupIndex) &&
      this.isCountToShowPaging(groupRowArgs.group)
    ) {
      this.lastGroupExpandIndex = groupRowArgs.groupIndex;
    }
  }

  isCountToShowPaging(group: GroupResult): boolean {
    const itemsCount = this.getGroupResultItemsCount(group);
    return itemsCount > this.startPagingFromItemsNumber;
  }

  onGroupChange() {
    this.resetGroupPaging();
  }

  getGroupResultItemsCount(group: GroupResult): number {
    if (
      !group ||
      !group.aggregates ||
      !group.aggregates.default ||
      !group.aggregates.default.count
    ) {
      return 0;
    }

    return group.aggregates.default.count;
  }

  /**
   * Returns the first non-null checked value from this group or any ancestor
   * (e.g. for "0_0" checks "0_0" then "0"). Use when first-loading a child
   * group to apply parent selection to the new records.
   */
  getAncestorChecked(groupIndex: string): boolean | null {
    let current: string | null = groupIndex;
    while (current != null) {
      const page = this.groupIndexAndLastPageUsed[current];
      if (page?.checked != null) return page.checked;
      current = current.includes('_') ? current.replace(/_\d+$/, '') : null;
    }
    return null;
  }

  /**
   * Runs a callback for every record in this group's page.data and every
   * descendant group's page.data. Use when applying parent selection so that
   * all loaded records under the group get the selection (e.g. for 2+ level
   * grouping, child group pages 2, 3, … are included).
   */
  forEachRecordInGroupAndDescendants(
    groupIndex: string,
    fn: (record: T) => void
  ): void {
    const page = this.getGroupPage(groupIndex);
    if (page?.data?.length) {
      page.data.forEach(fn);
    }
    const prefix = groupIndex + '_';
    for (const key of Object.keys(this.groupIndexAndLastPageUsed)) {
      if (key.startsWith(prefix)) {
        const p = this.groupIndexAndLastPageUsed[key];
        if (p?.data?.length) {
          p.data.forEach(fn);
        }
      }
    }
  }

  /**
   * Returns all records from every group's page.data (all pages). Use with
   * grid flattened items and dedupe by id for save/selection (e.g. getItems).
   */
  getAllLoadedRecordsFromPager(): T[] {
    let items: T[] = [];
    for (const key of Object.keys(this.groupIndexAndLastPageUsed)) {
      const pageData = this.groupIndexAndLastPageUsed[key];
      if (pageData?.data?.length) {
        items = items.concat(pageData.data);
      }
    }
    return items;
  }

  getGroupData(): Observable<ODataResult> | undefined {
    const page = this.getGroupPage(this.lastGroupExpandIndex);

    if (page?.data) {
      return of(
        ODataResult.FromDataResult({
          data: page.data.slice(page.skip, page.skip + page.take),
          total: page.totalCount ?? page.data.length,
        })
      );
    }
  }

  isGridGroupPagination(gridState: State, odataQuery: string): boolean {
    return (
      gridState.group?.length &&
      this.lastGroupExpandIndex &&
      !odataQuery.includes('$top') &&
      !odataQuery.includes('groupby((')
    );
  }
}
